import { useEffect, useMemo, useState } from 'react';

import Favicon from 'react-favicon';
import parse from 'html-react-parser';

import { useSelector } from 'react-redux';

import { Helmet } from 'react-helmet';
import { get, isEmpty } from 'lodash';
import { isContactUsFromPath, RootState } from '@share/utils';

import FaviconImage from '@assets/images/favicon.png';
import { useHistory } from 'react-router-dom';

const ORG_ID_TOEXCLUDE_CHAT = [644];

export function ApplicationHelmet() {

  const loginStore = useSelector((state: RootState) => state.loginStore);
  const menuStore = useSelector((state: RootState) => state.navigationMenuStore);
  const reviewBookStore = useSelector((state: RootState) => state.reviewBookStore);

  const history = useHistory();

  const { account, user } = loginStore;
  const { items } = menuStore;

  const faviconMenu = items?.favicon;
  const faviconPref = account?.faviconPref;
  const pageTitlePref = account?.pageTitlePref;
  const isRSITemplate = account?.isRSITemplate;
  const levartiOrgIds = account?.levartiOrgIds;
  const googleTagManagerCode = account?.googleTagManagerCode;
  const isAshevillePlay = account?.isAshevillePlay;
  const ashevillePlayGoogleTagManagerId = account?.ashevillePlayGoogleTagManagerId;

  const booking = reviewBookStore?.booking;
  const bookingComplete = reviewBookStore?.bookingComplete;

  const balancePaid = get(booking?.hotelSegments, '[0].totalPay', 1);
  const currency = get(booking?.hotelSegments, '[0].currency', 'USD');
  const bookingReference = get(booking?.hotelSegments, '[0].bookingReference', 'NA');

  const ashevillePlayScript = `https://www.googletagmanager.com/gtag/js?id=${ashevillePlayGoogleTagManagerId}`;

  const helmetScripts = get(account?.helmetScripts, '[0]');
  
  const displayMarketingScriptFlag = import.meta.env.VITE_DISPLAY_MARKETING_SCRIP;

  const favicon = useMemo(() => faviconMenu ? faviconMenu : !isEmpty(faviconPref) ? faviconPref : FaviconImage, [faviconPref]);
  
  // Check if Modern template should be used (both SiteId AND OrgId must match)
  const siteId = items?.siteId;
  const organizationId = items?.organizationId;
  const modernHomeTemplateOrgidList = account?.modernHomeTemplateOrgidList;
  const modernHomeTemplateSiteidList = account?.modernHomeTemplateSiteidList;
  const displayRsiClubChatEnabled = account?.displayRsiClubChatEnabled;
  const displayRsiClubChatRsiSiteidList = account?.displayRsiClubChatRsiSiteidList;
  const displayRsiClubChatRsiOrgidList = account?.displayRsiClubChatRsiOrgidList;

  const isInModernOrgId = modernHomeTemplateOrgidList?.includes(organizationId?.toString() as string);
  const isInModernSiteId = modernHomeTemplateSiteidList?.includes(siteId?.toString() as string);

  const useModernTemplate = useMemo(() => isRSITemplate && (
    (isInModernOrgId && isInModernSiteId) || 
    (account?.isModernTemplate)
  ), [isRSITemplate, isInModernOrgId, isInModernSiteId, siteId, organizationId]);

  const displayChat = useMemo(() => !items?.isMLM && isRSITemplate && items?.rsiId && !items?.isRemoveChat && !ORG_ID_TOEXCLUDE_CHAT.includes(items?.organizationId) && !levartiOrgIds?.includes(items?.organizationId) && !useModernTemplate, [isRSITemplate, items, levartiOrgIds, useModernTemplate]);
  
  const isContactUsPage = isContactUsFromPath(history);

  const isRsiClub = useMemo(() => isRSITemplate && 
                                  displayRsiClubChatEnabled && 
                                  (
                                    displayRsiClubChatRsiOrgidList?.includes(items?.organizationId?.toString() as string) ||
                                    displayRsiClubChatRsiSiteidList?.includes(items?.siteId?.toString() as string)), [isRSITemplate, displayRsiClubChatEnabled, displayRsiClubChatRsiOrgidList, displayRsiClubChatRsiSiteidList, items]);
  
  const initializeZendesk = () => {
    try {
      // @ts-ignore
      return zE;
    } catch (error) {}
    return undefined;
  };

  const zEInstance = initializeZendesk();
            
  useEffect(() => {
    if (isRsiClub && zEInstance) {
      if (!isContactUsPage) {
        zEInstance('messenger', 'close');
      } else {
        zEInstance('messenger', 'open');
      }
    }
  }, [isRsiClub, zEInstance, isContactUsPage]);
         
  return (
    <>
      <Favicon url={favicon as string} />
      
      <Helmet>
        {!isEmpty(pageTitlePref) ? (
          <title>{pageTitlePref}</title>) : null}

        {(isRSITemplate && displayMarketingScriptFlag === 'true') ? (
          <script type="text/javascript">
            {`(function() {
                window.sib = {
                  equeue: [],
                  client_key: "jyhjbffcot7rzilng6zj030p"
                };

                /* OPTIONAL: email for identify request*/
                // window.sib.email_id = 'example@domain.com';
                window.sendinblue = {};
                for (var j = ['track', 'identify', 'trackLink', 'page'], i = 0; i < j.length; i++) {
                  (function(k) {
                    window.sendinblue[k] = function() {
                      var arg = Array.prototype.slice.call(arguments);
                      (window.sib[k] || function() {
                        var t = {};
                        t[k] = arg;
                        window.sib.equeue.push(t);
                      })(arg[0], arg[1], arg[2], arg[3]);
                    };
                  })(j[i]);
                }

                var n = document.createElement("script"),
                    i = document.getElementsByTagName("script")[0];
                n.type = "text/javascript", n.id = "sendinblue-js", n.async = !0, n.src = "https://sibautomation.com/sa.js?key=" + window.sib.client_key, i.parentNode.insertBefore(n, i), window.sendinblue.page();
              })();`}
          </script>) : null}

        {displayChat ? (
          <script type="text/javascript">
            {`var url_string = window.location.href;
              var url = new URL(url_string);
              var CA_DID = url.searchParams.get("ca_did");
              var CA_APIKEY = url.searchParams.get("apiKey");
              var CA_TITLE = url.searchParams.get("title");
              var CA_THEME_COLOR = url.searchParams.get("themeColor");`}
          </script>) : null}

        {displayChat ? (
          <script type="text/javascript">
            {`var chatConfig={
                "widgetConfig": {
                    "title": CA_TITLE,
                    "subtitle": "Chat",
                    "headerLogo": "https://ozonetel.com/wp-content/uploads/2022/03/O-Image.png.webp",
                    "showHeaderLogo": true,
                    "showAgentIcon": false,
                    "showAgentAvatar": false
                },
                "CA_SCRIPT": "https://crexendocx.com/ChatWidget/dist/caChatAPI.js",
                "clientInfo": {
                    "DID": "8284453857",
                    "API_KEY": "KK7edafced1c078adc0856ff36a7111116"
                },
                "custDetails": {
                    "custName": "${user?.firstName} ${user?.lastName}",
                    "custMail": "${user?.email}",
                    "custPhone": "${items?.phone}",
                    "custId": "${user?.keyid}"
                },
                "theme": {
                    "primary": "#"+CA_THEME_COLOR,
                    "appHeader": {
                        "backgroundColor": "#FFFFFF",
                        "textColor": "#2F324A"
                    },
                    "conversation": {
                        "body": "#F1F2F9",
                        "messagePreview": "#ffff",
                        "text": "#2F324A",
                        "bot": {
                            "backgroundColor": "#FFFFFF",
                            "textColor": "#2F324A"
                        },
                        "user": {
                            "backgroundColor": "rgba(59, 141, 247, 1)",
                            "textColor": "#FFFFFF"
                        },
                        "interactiveButton": {
                            "backgroundColor": "rgba(59, 141, 247, 1)",
                            "textColor": "#FFFFFF"
                        }
                    }
                },
                "launcher": {
                    "type": "text",
                    "text": "Need Help? Chat with Us"
                },
                "userForm": [
                    {
                        "name": "Name",
                        "required": true,
                        "hide": false
                    },
                    {
                        "name": "Email",
                        "required": true,
                        "hide": false
                    },
                    {
                        "name": "CountryCode",
                        "required": false,
                        "hide": true
                    },
                    {
                        "name": "Phone",
                        "required": false,
                        "hide": false
                    },
                    {
                        "name": "Address",
                        "required": false,
                        "hide": true
                    }
                ],
            }`}
          </script>) : null}
          
        {displayChat ? (
          <script type="text/javascript" src="https://crexendocx.com/ChatWidget/dist/chatWidget.js"></script>) : null}

        {helmetScripts && parse(helmetScripts)}

        {googleTagManagerCode && (
          <script type="text/javascript">{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${googleTagManagerCode}');`}</script>)}

        {(isAshevillePlay && ashevillePlayGoogleTagManagerId) ? (
          <script async src={ashevillePlayScript}></script>) : null}

        {(isAshevillePlay && ashevillePlayGoogleTagManagerId) ? (
          <script>{`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', '${ashevillePlayGoogleTagManagerId}');`}
          </script>) : null}


        {(isAshevillePlay && ashevillePlayGoogleTagManagerId && bookingComplete && booking) ? (
          <script>{`
            gtag('event', 'conversion', {
                'send_to': '${ashevillePlayGoogleTagManagerId}/RRT_COG3hdsaENTPsYxA',
                'value': ${balancePaid},
                'currency': '${currency}',
                'transaction_id': '${bookingReference}'
            });`}
          </script>) : null}

        {isRsiClub ? (
          <script id="ze-snippet" src="https://static.zdassets.com/ekr/snippet.js?key=472d50d5-f068-4a13-98e8-13b5f4a4b714"> </script>) : null}

        {isRsiClub ? (
          <style>{`
            .ozcw-widget-container {
              display: none !important;
            }`}
          </style>) : null}
      </Helmet>
    </>);
}

